import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useContext, useMemo, useRef, useState } from 'react';
import { Prompt, useHistory } from 'react-router';
import { useDidUpdate, useStateInRef } from 'seagull';
import { useOverlayContext, useStore } from '~/contexts';
import { selectors } from '~/store/state-selectors';
const ManagerContext = /*#__PURE__*/ createContext({
    registerNavigationBackBlocker () {
        throw new Error('`registerNavigationBackBlocker` called before context initialization');
    }
});
export function useModalCloseManager() {
    return useContext(ManagerContext);
}
function useInternalManager() {
    const [blockers, setBlockers] = useState([]);
    const [cycleDone, setCycleDone] = useState(false);
    const shouldBlockRef = useRef(true);
    const { goBack } = useHistory();
    return useMemo(()=>({
            registerNavigationBackBlocker (blocker) {
                setBlockers(blockers.concat(blocker));
                return {
                    unregister () {
                        setBlockers(blockers.filter((fn)=>fn === blocker));
                    }
                };
            },
            shouldBlock () {
                if (blockers.length === 0) {
                    return false;
                }
                if (!cycleDone) {
                    shouldBlockRef.current = true;
                    Promise.all(blockers.map((blocker)=>blocker())).catch(()=>[
                            true
                        ]).then((values)=>{
                        setCycleDone(true);
                        shouldBlockRef.current = values.some((isBlock)=>isBlock);
                        if (!shouldBlockRef.current) {
                            setTimeout(()=>{
                                goBack();
                            }, 0);
                        }
                    });
                }
                if (!shouldBlockRef.current) {
                    setCycleDone(false);
                }
                return shouldBlockRef.current;
            }
        }), [
        blockers,
        cycleDone,
        goBack
    ]);
}
export function ModalCloseManagerProvider(param) {
    let { children } = param;
    const { getOpenedModals, hideAllModals } = useOverlayContext();
    const internalManager = useInternalManager();
    const hasUserInfo = useStore(selectors.user.hasUserInfo);
    const getInternalManager = useStateInRef(internalManager);
    const value = useMemo(()=>{
        return {
            registerNavigationBackBlocker: (blocker)=>getInternalManager().registerNavigationBackBlocker(blocker)
        };
    }, []);
    useDidUpdate(()=>{
        if (!hasUserInfo) {
            hideAllModals();
        }
    }, [
        hasUserInfo
    ]);
    return /*#__PURE__*/ _jsxs(ManagerContext.Provider, {
        value: value,
        children: [
            /*#__PURE__*/ _jsx(Prompt, {
                when: true,
                message: (_, action)=>{
                    const modals = getOpenedModals();
                    if (modals.length > 0 && action !== 'REPLACE') {
                        if (action === 'POP' && internalManager.shouldBlock()) {
                            return false;
                        }
                        hideAllModals();
                    }
                    return true;
                }
            }),
            children
        ]
    });
}
